import { useState, type ReactNode } from 'react';
import { Button } from './Button.js';
import { Field } from './Field.js';
import { StatusPill } from './StatusPill.js';
import { TextInput } from './TextInput.js';
import { Toolbar } from './Card.js';

/**
 * A stored secret: whether one is set, what it looks like, and how to replace
 * it. The APIs pane had four of these written out four times, each phrasing
 * "is there one" differently in a sentence ("Current: not set", "Secret is
 * set", "Currently: set.") and none of them reporting a failed save — a
 * rejected key save was completely silent.
 *
 * The value is never echoed back by the server, so this is always "replace",
 * never "edit": the field starts empty whatever is stored.
 */
export function SecretField({
  label,
  hint,
  preview,
  isSet,
  placeholder,
  mask = true,
  saveLabel = 'Save',
  extra,
  disabled = false,
  onSave,
  onRemove,
  removeLabel = 'Remove',
}: {
  label: string;
  hint?: ReactNode | undefined;
  /** A redacted echo of what's stored, when the server offers one. */
  preview?: string | null | undefined;
  isSet: boolean;
  placeholder?: string | undefined;
  mask?: boolean | undefined;
  saveLabel?: string | undefined;
  extra?: ReactNode | undefined;
  disabled?: boolean | undefined;
  onSave: (value: string) => Promise<unknown>;
  onRemove?: (() => void) | undefined;
  removeLabel?: string | undefined;
}) {
  const [value, setValue] = useState('');
  const [state, setState] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setState(null);
    onSave(value).then(
      () => {
        setValue('');
        setBusy(false);
        setState({ tone: 'ok', text: 'Saved.' });
      },
      (err: unknown) => {
        setBusy(false);
        setState({ tone: 'error', text: err instanceof Error ? err.message : 'could not save' });
      },
    );
  };

  return (
    <form onSubmit={submit}>
      <div className="ui-secret__state">
        <StatusPill tone={isSet ? 'ok' : 'neutral'}>{isSet ? 'Set' : 'Not set'}</StatusPill>
        {preview ? <code className="mono">{preview}</code> : null}
      </div>
      {extra}
      {/* The label stays as-is whether or not one is stored: lowercasing it into
          "Replace anthropic api key" read like a typo. The pill above already
          says whether there is one, and the empty field says it's a replacement. */}
      <Field label={label} hint={hint}>
        {(aria) => (
          <TextInput
            {...aria}
            type={mask ? 'password' : 'text'}
            reveal={mask}
            autoComplete="off"
            placeholder={placeholder}
            value={value}
            disabled={disabled}
            onChange={(e) => setValue(e.target.value)}
          />
        )}
      </Field>
      <Toolbar className="cfg-actions">
        <Button type="submit" loading={busy} disabled={disabled || !value.trim()}>
          {saveLabel}
        </Button>
        {isSet && onRemove ? (
          <Button variant="danger" onClick={onRemove} disabled={disabled}>
            {removeLabel}
          </Button>
        ) : null}
        {state ? (
          <span
            className={state.tone === 'error' ? 'ui-field__error' : 'ui-saved ui-saved--ok'}
            role="status"
          >
            {state.text}
          </span>
        ) : null}
      </Toolbar>
    </form>
  );
}
